import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { WizardNavigationComponent } from '../../../../shared/components/wizard-navigation/wizard-navigation.component';
import { NumberInputComponent, TextInputComponent } from 'ddmo-storybook-lib';
import { WorkHistoryService, WorkHistory } from '../../services/work-history.service';
import { CredentialDocument } from '../dental-credentials/dental-credentials.component';
import { DocumentCategory, DocumentSubCategory, Specialities } from '../../../../shared/constants/document.constants';
import { DentalCredentialsService } from '../../services/dental-credentials.service';
import { demographicsFieldsConfig, locationFieldsConfig } from './validation-config';
import { ConfirmationPopupComponent, PdfViewerDialogComponent } from '../../../popups/processing/processing.component';
import { MatDialog } from '@angular/material/dialog';
import { SharedService } from '../../../../shared/services/shared.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { PreviewModalComponent } from '../../../landing/pages/preview/preview-model.component';
import { LoaderService } from '../../../../shared/services/loader.service';

const CONTROLLED_SUBSTANCE_EXPIRATION_MESSAGE = 'Expiration date must be 30 days from date of submission';
const MALPRACTICE_EXPIRATION_MESSAGE = 'Expiration date must be at least 30 days from submission';
const VALIDATE_ALL_DATA_DEMOGRAPHICS_MESSAGE = 'Please save your demographic information before submitting.';

interface Document {
    title: string;
    categoryId: number;
    subCategoryId: number;
    subtitle: string;
    isSigned?: boolean;
    name?: string;
    originalFileName?: string;
    date?: string;
    hide?: boolean;
}

@Component({
    selector: 'app-confirmation',
    imports: [CommonModule, FormsModule, ReactiveFormsModule, WizardNavigationComponent, TextInputComponent,
        NumberInputComponent, MatIconModule],
    templateUrl: './confirmation.component.html',
    styleUrl: './confirmation.component.scss'
})
export class ConfirmationComponent implements OnInit, OnDestroy {
    private readonly MIN_EXPIRATION_DAYS_FROM_SUBMISSION = 30;

    isManageLocationsFlow = false;
    accordionStates = {
        dentistDemographics: false,
        dentalCredentials: false,
        workHistory: false,
        attestations: false,
        signedDocuments: false,
        location: false,
        tin: false
    };
    isConfirmationPage = "no"
    public dentistInfoForm = new FormGroup(({
        dentistName: new FormControl(''),
        licenseNumber: new FormControl('')
    }))
    workHistories: WorkHistory[] = [];
    workHistoryErrors: any[] = [];
    demographicsErrors: any[] = [];
    locationErrors: any[] = [];
    tinErrors: any[] = [];
    isDentalCredentialInvalid = false;
    attestErrors: any = {
        "questionFormError": false,
        "attestationCheckboxError": false,
        "fullNameError": false,
        "noRecord": false,
        "inValid": false
    }
    attesatationData: any
    attesatationResponseData: any
    isSignedDocumentsInvalid = false;
    isDemographicsInvalid = false;
    dentistCredentials: any;
    demographicsData: any;
    demographicsFieldConfig: any = demographicsFieldsConfig;
    locations: any = [];
    locationFieldConfig: any = locationFieldsConfig
    directoryInfo: any = [];
    ishasParLocation = false;
    ishasPrimaryLocation = false;
    showParLocationError = false
    signedDocuments: Document[] = [
        {
            title: 'W9',
            categoryId: DocumentCategory.SignedDocument,
            subCategoryId: DocumentSubCategory.W9Form,
            subtitle: 'Required for onboarding',
            isSigned: false,
        },
        {
            title: 'Participating agreement',
            categoryId: DocumentCategory.SignedDocument,
            subCategoryId: DocumentSubCategory.ParticipatingAgreement,
            subtitle: 'Required for onboarding',
            isSigned: false,
        },
        {
            title: 'Uniform requirements',
            categoryId: DocumentCategory.SignedDocument,
            subCategoryId: DocumentSubCategory.UniformRequirements,
            subtitle: 'Required for onboarding',
            name: 'ddmo_uniform_requirements.pdf',
            isSigned: false,
        }
    ];
    credentialsErros: any;

    credentialDocumentsMap: Record<string, CredentialDocument[]> = {
        STATE_LICENSE: [],
        CDS: [],
        DEA: [],
        MALPRACTICE: [],
        SPECIALTY: [],
        ANESTHESIA: [],
        ADDITIONAL1: [],
        ADDITIONAL2: [],
        ADDITIONAL3: [],
    };
    reqPayload: any = {};
    fullName: any = '';

    constructor(private router: Router, private workHistoryService: WorkHistoryService,
        private route: ActivatedRoute, private dentalCredentialingService: DentalCredentialsService,
        private fb: FormBuilder, public dialog: MatDialog, private sharedService: SharedService, private loader: LoaderService, private service: SharedService,
    ) {
        const demographics = JSON.parse(localStorage.getItem("demographics") || '{}')
        this.sharedService.username$.pipe(takeUntilDestroyed()).subscribe(name => {
            if (!demographics?.firstName && !demographics?.lastName) {
                this.fullName = name;
                this.dentistInfoForm.patchValue({
                    dentistName: this.fullName
                })
            }
        });
    }

    ngOnInit(): void {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        const loggedUserDetails = JSON.parse(localStorage.getItem("loggedUserDetails") || '{}')
        const demographics = JSON.parse(localStorage.getItem("demographics") || '{}')
        if (demographics && demographics && (demographics['firstName'] && (demographics['lastName']))) {
            let lastName = demographics['lastName'] ? demographics['lastName'] : '';
            this.fullName = `${demographics['firstName']} ${lastName}`
        } else {
            this.fullName = (loggedUserDetails['firstName'] && loggedUserDetails['lastName']) ? `${loggedUserDetails['firstName']} ${loggedUserDetails['lastName']}` : ""
        }
        this.route.queryParams.subscribe((params: any) => {
            this.isConfirmationPage = "yes"
            localStorage.setItem("isConfirmationPage", this.isConfirmationPage)
            this.checkAttestErrors()
            if (loggedUserDetails) {
                this.dentistInfoForm.patchValue({
                    dentistName: this.fullName,
                    licenseNumber: loggedUserDetails['licenseNumber']
                })
                this.dentistInfoForm.controls['dentistName'].disable();
                this.dentistInfoForm.controls['licenseNumber'].disable();
                this.reqPayload = {
                    userId: loggedUserDetails['userId'],
                    licenseNumber: loggedUserDetails['licenseNumber'],
                    npi: loggedUserDetails['npi']
                }
            }

            this.workHistoryService.workHistories$.subscribe((histories: any) => {
                this.workHistories = histories;
                if (this.workHistories.length == 0) {
                    this.workHistoryErrors.push({
                        error: 'At least one Work History or School entry must be added before submitting your application.'
                    })
                }
            });
            //Signed documents & Dental credential patching
            if (JSON.parse(localStorage.getItem('credential')!)?.length || JSON.parse(localStorage.getItem('documents')!)?.length) {
                const payload = {
                    credentials: JSON.parse(localStorage.getItem('credential')!) || [],
                    documents: JSON.parse(localStorage.getItem('documents')!) || []
                }
                this.patchValues(payload);
            } else {
                this.validateDocuments();
            }
            const result: any = this.validateDentalConfirmation();
            if (!result.isValid) {
                this.credentialsErros = result.errors;
                this.isDentalCredentialInvalid = true;
            } else {
                this.credentialsErros = [];
                this.isDentalCredentialInvalid = false;
            }
            this.demographicsData = JSON.parse(localStorage.getItem("demographics") || '{}')
            const storedLocations = localStorage.getItem('locations');
            this.locations = storedLocations ? JSON.parse(storedLocations) : [];
            const directoryData = localStorage.getItem('locationDirectories');
            this.directoryInfo = directoryData ? JSON.parse(directoryData) : [];
            this.getDemographicsErrors();
            this.getLocationErrors();

        })
    }

    toggleAccordion(section: keyof typeof this.accordionStates) {
        this.accordionStates[section] = !this.accordionStates[section];
    }

    onPrevious() {
        this.router.navigate(['/credentialing/sign-documents']);
    }

    onFinish() {
        this.confirmationPopupCall()
    }

    ngOnDestroy(): void {
        this.isConfirmationPage = "no"
        localStorage.removeItem("isConfirmationPage")
    }

    patchValues(results: any) {
        if (results?.credentials?.length) {
            this.dentistCredentials = results.credentials[0];
        }
        this.credentialDocumentsMap = {
            STATE_LICENSE: [],
            CDS: [],
            DEA: [],
            MALPRACTICE: [],
            SPECIALTY: [],
            ANESTHESIA: [],
            ADDITIONAL1: [],
            ADDITIONAL2: [],
            ADDITIONAL3: [],
        };
        for (const doc of results['documents']) {
            this.insertDocumentMap(doc, true);
        }
        const isRecred = this.getAction();
        this.validateDocuments();
    }

    validateDocuments() {
        this.signedDocuments[0].hide = true;
        this.signedDocuments[1].hide = false;
        const isParticipatingAgreementRequired = !this.signedDocuments[1]?.isSigned;
        this.isSignedDocumentsInvalid = (isParticipatingAgreementRequired ||
            (!this.signedDocuments[2]?.isSigned || false)) ? true : false;
    }

    insertDocumentMap(doc: any, isRecred: boolean) {
        switch (doc.subCategoryId) {
            case DocumentSubCategory.StateLicense:
                this.credentialDocumentsMap['STATE_LICENSE'].push({
                    originalFileName: `${doc.fileName}.pdf`,
                    name: `State_Licence.pdf`,
                });
                break;

            case DocumentSubCategory.CDSLicense:
                this.credentialDocumentsMap['CDS'].push({
                    originalFileName: `${doc.fileName}.pdf`,
                    name: `CDS_License.pdf`,
                });
                break;

            case DocumentSubCategory.DEALicense:
                this.credentialDocumentsMap['DEA'].push({
                    originalFileName: `${doc.fileName}.pdf`,
                    name: `DEA_License.pdf`,
                });
                break;

            case DocumentSubCategory.MalpracticeInsuranceLicense:
                this.credentialDocumentsMap['MALPRACTICE'].push({
                    originalFileName: `${doc.fileName}.pdf`,
                    name: `Malpractice_Insurance_Lincense.pdf`,
                });
                break;

            case DocumentSubCategory.SpecialtyLicense:
                this.credentialDocumentsMap['SPECIALTY'].push({
                    originalFileName: `${doc.fileName}.pdf`,
                    name: `Speciality_License.pdf`,
                });
                break;

            case DocumentSubCategory.AnesthesiaSedationLicense:
                this.credentialDocumentsMap['ANESTHESIA'].push({
                    originalFileName: `${doc.fileName}.pdf`,
                    name: `Anesthesia_Sedation_License.pdf`,
                });
                break;

            case DocumentSubCategory.AdditionalDocument1:
                this.credentialDocumentsMap['ADDITIONAL1'].push({
                    originalFileName: `${doc.fileName}.pdf`,
                    name: `Additional_Document_1.pdf`,
                });
                break;

            case DocumentSubCategory.AdditionalDocument2:
                this.credentialDocumentsMap['ADDITIONAL2'].push({
                    originalFileName: `${doc.fileName}.pdf`,
                    name: `Additional_Document_2.pdf`,
                });
                break;

            case DocumentSubCategory.AdditionalDocument3:
                this.credentialDocumentsMap['ADDITIONAL3'].push({
                    originalFileName: `${doc.fileName}.pdf`,
                    name: `Additional_Document_3.pdf`,
                });
                break;

            case DocumentSubCategory.W9Form:
                this.signedDocuments[0].isSigned = doc.isSigned;
                this.signedDocuments[0].originalFileName = `${doc.fileName}.pdf`;
                this.signedDocuments[0].name = `W9Form.pdf`;
                this.signedDocuments[0].hide = true;
                this.signedDocuments[0].date = doc.isSigned ? (doc?.fileName?.split('_')[0].replace(
                    /(\d{4})(\d{2})(\d{2})/,
                    '$1-$2-$3'
                )) : 'Not signed';
                break;

            case DocumentSubCategory.ParticipatingAgreement:
                this.signedDocuments[1].isSigned = doc.isSigned;
                this.signedDocuments[1].originalFileName = `${doc.fileName}.pdf`;
                this.signedDocuments[1].name = `participatingAgreement.pdf`;
                this.signedDocuments[1].hide = false;
                this.signedDocuments[1].date = doc.isSigned ? (doc?.fileName?.split('_')[0].replace(
                    /(\d{4})(\d{2})(\d{2})/,
                    '$1-$2-$3'
                )) : 'Not signed';
                break;

            case DocumentSubCategory.UniformRequirements:
                if (doc.isSigned) {
                    this.signedDocuments[2].isSigned = doc.isSigned;
                    this.signedDocuments[2].originalFileName = `${doc.fileName}.pdf`;
                    this.signedDocuments[2].name = 'ddmo_uniform_requirements.pdf';
                    this.signedDocuments[2].hide = false;
                    this.signedDocuments[2].date = doc.isSigned ? (doc?.fileName?.split('_')[0].replace(
                        /(\d{4})(\d{2})(\d{2})/,
                        '$1-$2-$3'
                    )) : 'Not signed';
                }
                break;
        }
    }

    onViewDocument(doc: any, filename: any) {
        filename = doc?.title?.toLocaleLowerCase() != `uniform requirements` ? filename.split(".")[0] : filename;
        this.dentalCredentialingService.viewAzureDocument(filename).subscribe({
            next: (response: any) => {
                const url = window.URL.createObjectURL(response.body);
                const link = document.createElement('a');
                link.href = url;
                link.download = ((doc?.isSigned && doc?.name != `uniformRequirements.pdf`) ?
                    'Signed_' : '') + `${doc?.title?.toLocaleLowerCase() == `uniform requirements` ?
                        doc.name : `uniformRequirements`}`;
                link.click();
                window.URL.revokeObjectURL(url);
            }, error: (err: any) => {
                this.sharedService.logError("APIFailed", "FileConfirmationComponent - viewAzureDocument, Method:onViewDocument", this.router.url.split('?')[0], err).subscribe();
            }
        })
    }

    onCancel() {
        this.router.navigate(['/landing']);
    }

    getFieldError(field: any): string | null {

        const value = this.demographicsData?.[field.key];

        if (field.required && !value) {
            return 'Required';
        }

        if (field.length && value && value.length !== field.length) {
            return `Must be ${field.length} digits`;
        }

        if (field.pattern && value && !field.pattern.test(value)) {
            return 'Invalid format';
        }

        if (field.key === 'DOB' && value) {
            if (!this.isMinAge(value, 18)) {
                return 'Must be at least 18 years old';
            }
        }

        if (field.key === 'yearGraduated' && value) {
            if (!this.isPastOrTodayDate(value)) {
                return 'Cannot be a future date';
            }
        }
        if (field.key === 'otherSchool') {

            if (
                (this.demographicsData?.dentalSchool === 'My School Is Not Listed' || this.demographicsData?.dentalSchool === 'My school is not listed' || this.demographicsData?.dentalSchool === 'other'
                    || this.demographicsData?.dentalSchool === 'Other' || this.demographicsData?.dentalSchool === 'OTHER'
                ) &&
                !value?.trim()
            ) {
                return 'Required';
            }
        }

        return null;
    }

    getDemographicsErrors() {
        this.demographicsErrors = this.demographicsFieldConfig
            .map((field: any) => ({
                label: field.label,
                error: this.getFieldError(field)
            }))
            .filter((field: any) => field.error);
    }
    checkishasPrimaryLocation(location: any) {
        if (location.isPrimary == true) {
            this.ishasPrimaryLocation = true
            return true
        } else { return false }
    }

    validateLocation(location: any, tinData: any): { label: string; error: string }[] {
        const errors: { label: string; error: string }[] = [];
        let isExistingPar = this.setParticipating(location);
        let ishasPrimaryLocation = this.checkishasPrimaryLocation(location);
        if(ishasPrimaryLocation){
            this.ishasPrimaryLocation = true
        }
        if (isExistingPar) {
            this.ishasParLocation = true
            const isRecred = this.getAction();
            this.validateDocuments()
        }
        this.locationFieldConfig.forEach((field: any) => {
            const value = this.getFieldValue(location, field);
            if (field.required && (value === null || value === undefined || value === '')) {
                if (field.key == 'startPracticingAt' && location.isParticipating != true && location.isLocationFromTINSearch === true) {
                    errors.push({ label: field.label, error: 'Required' });
                }else if (field.key == 'startPracticingAt' && location.isParticipating != true && location.stillPracticing == true && location.isLocationFromTINSearch == null) {
                    errors.push({ label: field.label, error: 'Required' });
                }
                else if (field.key == 'endedPracticingAt' && location.stillPracticing == false && location.isParticipating != true && location.isLocationFromTINSearch == null) {
                    errors.push({ label: field.label, error: 'Required' });
                } 
                else {
                    if (field.key != 'startPracticingAt' && field.key != 'endedPracticingAt') {
                        errors.push({ label: field.label, error: 'Required' });
                    }
                }
            }

            if (field.length && value && value.length !== field.length) {
                errors.push({ label: field.label, error: `Must be ${field.length} characters` });
            }

            if (field.pattern && value && !field.pattern.test(value)) {
                errors.push({ label: field.label, error: 'Invalid format' });
            }
        });

        if (this.hasTinEffectiveDateAfterLocationStartDate(location, tinData)) {
            errors.push({
                label: 'TIN effective date',
                error: 'Must be on or before Date started'
            });
        }

        if (((!location.esLocationId && !location.isLocationFromTINSearch) && location.isParticipating == true) ||
            (location.esLocationId && location.isParticipating == true && isExistingPar == false)
            || (location.isLocationFromTINSearch && location.isParticipating == true)) {
            if (!location.dcaDocumentId) {
                errors.push({ label: 'Corporate authority form', error: 'Required' });
            }
        }
        if ((!location.esLocationId && !location.isLocationFromTINSearch && location.isPrimary == true) || (location.esLocationId && isExistingPar == false && location.isParticipating == true && location.isPrimary == true)
            || (location.esLocationId && isExistingPar == true && location.stillPracticing != false && location.isPrimary == true)
            || (location.isLocationFromTINSearch && isExistingPar == true && location.isPrimary == true)) {
            const hasDirectoryError = this.validateDirectory(location.locationId);
            if (hasDirectoryError) {
                errors.push({
                    label: 'Locations directory questions',
                    error: 'One or more questions have not been answered'
                });
            }
        }
        return errors;
    }
    private getFieldValue(location: any, field: any): any {
        const primary = location[field.key];
        if (primary !== null && primary !== undefined && primary !== '') {
            return primary;
        }
        if (field.altKeys?.length) {
            for (const altKey of field.altKeys) {
                const altValue = location[altKey];
                if (altValue !== null && altValue !== undefined && altValue !== '') {
                    return altValue;
                }
            }
        }
        return primary; 
    }

    private hasTinEffectiveDateAfterLocationStartDate(location: any, tinData: any[]): boolean {
        if (localStorage.getItem('isnewTIN') !== 'true') {
            return false;
        }

        const locationStartDate = this.toComparableDate(location?.startPracticingAt ?? location?.startDate);
        if (!locationStartDate) {
            return false;
        }

        const locationTin = this.normalizeTinIdentifier(location?.tin ?? location?.taxId);
        if (!locationTin) {
            return false;
        }

        const matchingTin = (tinData || []).find((item: any) => {
            const itemTin = this.normalizeTinIdentifier(item?.taxId ?? item?.tin);
            return itemTin && itemTin === locationTin;
        });

        if (!matchingTin) {
            return false;
        }

        const tinEffectiveDate = this.toComparableDate(
            matchingTin?.effectiveDate ?? matchingTin?.taxEntityEffectiveDate
        );

        if (!tinEffectiveDate) {
            return false;
        }

        return tinEffectiveDate > locationStartDate;
    }

    private normalizeTinIdentifier(value: any): string {
        if (value === null || value === undefined) {
            return '';
        }
        const normalized = `${value}`.trim();
        if (!normalized) {
            return '';
        }
        const digitsOnly = normalized.replace(/[^0-9]/g, '');
        return digitsOnly || normalized;
    }

    private toComparableDate(value: any): Date | null {
        if (!value) {
            return null;
        }
        const rawValue = `${value}`.trim();
        const normalizedValue = /^\d{8}$/.test(rawValue)
            ? `${rawValue.slice(0, 4)}-${rawValue.slice(4, 6)}-${rawValue.slice(6, 8)}`
            : value;

        const date = new Date(normalizedValue);
        if (isNaN(date.getTime())) {
            return null;
        }

        date.setHours(0, 0, 0, 0);
        return date;
    }
    getLocationErrors() {
        this.getTin().then((tinData: any) => {
            this.ishasParLocation = false;
            this.ishasPrimaryLocation = false;
            this.showParLocationError = false;

            this.locationErrors = this.locations
                .map((location: any, index: any) => ({
                    index: index + 1,
                    errors: this.validateLocation(location, tinData)
                }))
                .filter((loc: any) => loc.errors.length > 0);

            const activeLocations = this.locations.filter((location: any) => this.isLocationActive(location));
            this.ishasParLocation = activeLocations.some((location: any) => this.checkLocationIsPar(location) === true);
            this.ishasPrimaryLocation = activeLocations.some((location: any) => location?.isPrimary === true);

            // Build unique TIN validation
            this.tinErrors = [];

            const uniqueTins = [...new Map(
                tinData.map((item: any) => [item.taxId, item])
            ).values()];

            this.tinErrors = uniqueTins
                .filter((tin: any) => this.getTinHasParticipatingLocation(tin.taxId) && !tin.w9DocumentID)
                .map((tin: any) => ({
                    taxId: tin.taxId,
                    errors: [
                        {
                            label: 'W9',
                            error: 'Required'
                        }
                    ]
                }));

            if ((!this.ishasParLocation || !this.ishasPrimaryLocation)) {
                this.showParLocationError = true;
            }
        });
    }
        getTinHasParticipatingLocation(tin:any): boolean {
        const locations = JSON.parse(localStorage.getItem('locations') || '[]');
        const tinHasparticipatingLoc = locations.some((loc: any) => (loc.taxId ? loc.taxId : loc.tin) === tin && loc.isParticipating === true);
        return tinHasparticipatingLoc;
    }

    private isLocationActive(location: any): boolean {
        const hasStillPracticingValue = location?.stillPracticing !== null &&
            location?.stillPracticing !== undefined &&
            location?.stillPracticing !== '';

        if (hasStillPracticingValue) {
            return location.stillPracticing === true;
        }

        return true;
    }

    private isRecredUser(): boolean {
        return (localStorage.getItem('Action') || '').toLowerCase() === 'recred';
    }

    setParticipating(selectedLoc: any) {
        let network = selectedLoc.providerNetwork;
        let isLocationFromTinSearch = selectedLoc.isLocationFromTINSearch ? selectedLoc.isLocationFromTINSearch : ""
        const whLocations: any[] = JSON.parse((localStorage.getItem('whLocations') || '[]'))
        if (!network && selectedLoc.esLocationId) {
            const match = whLocations?.find(
                (wh: any) => wh.esLocationId === selectedLoc.esLocationId
            );
            network = match?.providerNetwork;
        }
        if (isLocationFromTinSearch || !selectedLoc?.esLocationId) {
            if (selectedLoc.isParticipating == true) {
                return true
            } else { return false }
        }
        if (network == 'Participating') { return true; }
        if (network == 'Not participating') { return false; }
        return null;
    }
    checkLocationIsPar(location: any) {
        if(location && location.isParticipating == true) { return true }
        if(location && location.isParticipating == false) { return false }
        return null;  
    }

    checkAttestErrors() {
        let attestationResponses = localStorage.getItem('attestationResponses');
        this.attesatationResponseData = attestationResponses ? JSON.parse(attestationResponses) : [];
        let attesatationData = localStorage.getItem('attestation');
        this.attesatationData = attesatationData ? JSON.parse(attesatationData) : {};
        if (!this.attesatationResponseData ||
            this.attesatationResponseData.length === 0 ||
            !this.attesatationData ||
            Object.keys(this.attesatationData).length === 0
        ) {
            this.attestErrors['noRecord'] = true;
            return;
        }
        const isQuestionFormError = this.attesatationResponseData?.some((answer: any) => {
            if (answer.response === true) {
                return !answer.explanation || answer.explanation.trim() === "";
            }
            if (answer.response === null) {
                return true;
            }
            return false;
        });
        this.attestErrors['questionFormError'] = isQuestionFormError;
        this.attestErrors['attestationCheckboxError'] = !this.attesatationData['attestedAckCertify'];
        this.attestErrors['fullNameError'] = !this.attesatationData['attestedBy'] || this.attesatationData['attestedBy'].trim() === "";
        this.attestErrors['inValid'] = (this.attestErrors['questionFormError'] || this.attestErrors['attestationCheckboxError'] || this.attestErrors['fullNameError'])
    }

    isEmpty(value: any): boolean {
        return (
            value === null ||
            value === undefined ||
            (typeof value === 'string' && value.trim() === '')
        );
    }

    validateDirectory(locationId: number): boolean {

        const directory = this.directoryInfo.find(
            (d: any) => Number(d.locationId) === locationId
        );

        if (!directory) return true;

        const requiredQuestions = ['hasWeekendHours', 'hasEarlyMorningHours', 'hasLateEveningHours',
            'hasEmergencyPhoneNumber', 'hasPublicTransitNearby', 'hasDisabledAdultCare', 'hasDisabledChildrenCare'];

        const hasMissingRadio = requiredQuestions.some(
            key => this.isEmpty(directory[key])
        );

        if (hasMissingRadio) return true;
        if (directory.hasEmergencyPhoneNumber == true) {
            let phonePattern = /^\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/
            if (this.isEmpty(directory.emergencyPhoneNumber) || !phonePattern.test(directory.emergencyPhoneNumber)) return true;
        }

        return false;
    }
    private isFutureDate(dateValue: any): boolean {
        if (!dateValue) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const inputDate = new Date(dateValue);

        if (isNaN(inputDate.getTime())) {
            return false;
        }
        return inputDate > today;
    }

    private isBeyondSubmissionThreshold(dateValue: any, minDaysFromSubmission: number): boolean {
        if (!dateValue) return false;
        const submissionDate = new Date();
        submissionDate.setHours(0, 0, 0, 0);

        const minimumAllowedDate = new Date(submissionDate);
        minimumAllowedDate.setDate(minimumAllowedDate.getDate() + minDaysFromSubmission);

        const inputDate = new Date(dateValue);
        inputDate.setHours(0, 0, 0, 0);
        if (isNaN(inputDate.getTime())) {
            return false;
        }

        return inputDate >= minimumAllowedDate;
    }

    private parseCurrencyAmount(value: any): number | null {
        if (this.isEmpty(value)) {
            return null;
        }

        const normalizedValue = String(value).replace(/[$,\s]/g, '');
        if (!normalizedValue) {
            return null;
        }

        const numericValue = Number(normalizedValue);
        return Number.isFinite(numericValue) ? numericValue : null;
    }

    confirmationPopupCall() {
        this.loader.show()
        const iserror = document.getElementsByClassName('attestations')
        let hasError = false
        if (iserror.length > 0) {
            hasError = true
            this.showAlert(hasError);
        }
        else {
            this.sharedService.validateData(this.reqPayload).subscribe({
                next: (res) => {
                    if (res.status == "SUCCESS") {
                        hasError = false
                        this.showAlert(hasError);
                    }
                    else {
                        hasError = true
                        this.showAlert(hasError, this.getValidateAllDataMessage(res));
                    }
                },
                error: (err) => {
                    this.sharedService.logError("APIFailed", "FileConfirmationComponent - validateData, Method:confirmationPopupCall", this.router.url.split('?')[0], err).subscribe();
                    this.showAlert(true, this.getValidateAllDataMessage(err?.error || err));
                }
            })
        }
    }


    private getValidateAllDataMessage(response: any): string {
        if (response?.errors?.demographics?.length) {
            return VALIDATE_ALL_DATA_DEMOGRAPHICS_MESSAGE;
        }
        return `Please review the fields in red and ensure all required information is complete and correct before submitting the application.`;
    }

    showAlert(hasError: any, customErrorMessage?: string) {
        let alertMessage = `Are you sure you want to submit? Please confirm that all entered details are correct.`;
        if (hasError == true) {
            alertMessage = customErrorMessage || `Please review the fields in red and ensure all required information is complete and correct before submitting the application.`
        }
        this.loader.hide()
        let dialogRef = this.dialog.open(ConfirmationPopupComponent, {
            disableClose: true,
            data: { message: alertMessage, hasError: hasError },
            width: '550px',
            backdropClass: '',
            hasBackdrop: true,
        });
        dialogRef.afterClosed().subscribe((result: any) => {
            if (result == true && hasError == false) {
                this.router.navigate(['/landing']);
            }
        });
    }
    validateDentalConfirmation(): {
        isValid: boolean;
        errors: { label: string; message: string }[];
    } {

        const errors: { label: any; message: any }[] = [];

        const credential = JSON.parse(localStorage.getItem('credential') || '[]')[0] || {};
        const hasMeaningfulValue = (value: any): boolean =>
            value !== null && value !== undefined && `${value}`.trim() !== '';
        const isCdsSelected =
            credential.cds === true ||
            credential.cds === 'true' ||
            hasMeaningfulValue(credential.cdsLicenseNumber) ||
            hasMeaningfulValue(credential.cDSLicenseNumber) ||
            hasMeaningfulValue(credential.cDSExpirationDate);
        const isDeaSelected =
            credential.dea === true ||
            credential.dea === 'true' ||
            hasMeaningfulValue(credential.deaLicenseNumber) ||
            hasMeaningfulValue(credential.dEALicenseNumber) ||
            hasMeaningfulValue(credential.dEAExpirationDate);
        const cdsLicenseNumber = credential.cdsLicenseNumber ?? credential.cDSLicenseNumber;
        const deaLicenseNumber = credential.deaLicenseNumber ?? credential.dEALicenseNumber;
        const userDetails = JSON.parse(localStorage.getItem('loggedUserDetails') || '{}');
        const demographics = JSON.parse(localStorage.getItem('demographics') || '{}');
        const hasDoc = (key: string) =>
            this.credentialDocumentsMap[key] &&
            this.credentialDocumentsMap[key].length > 0;

        if (!credential.stateLicenseNumber) {
            errors.push({ label: 'State license number', message: 'Required' });
        } else {
            if (userDetails.licenseState === 'SC') {
                if (!/^[0-9]{4,5}$/.test(credential.stateLicenseNumber)) {
                    errors.push({ label: 'State license number', message: 'Must be 4–5 digits only' });
                }
            } else {
                if (!/^[0-9]{5,10}$/.test(credential.stateLicenseNumber)) {
                    errors.push({ label: 'State license number', message: 'Must be 5–10 digits only' });
                }
            }
        }

        if (!hasDoc('STATE_LICENSE')) {
            errors.push({ label: 'State license document', message: 'Document is required' });
        }
        if (!credential.stateLicenseEffectiveDate) {
            errors.push({ label: 'State license effective date', message: 'Required' });
        } else if (this.isFutureDate(credential.stateLicenseEffectiveDate)) {
            errors.push({ label: 'State license effective date', message: 'Must be today or past date' });
        }
        if (isCdsSelected) {

            if (!cdsLicenseNumber) {
                errors.push({ label: 'CDS license number', message: 'Required' });
            } else if (!/^[A-Za-z0-9-]{1,10}$/.test(cdsLicenseNumber)) {
                errors.push({ label: 'CDS license number', message: 'Must between 1 to 10 alphanumeric characters or hyphens' });
            }

            if (!credential.cDSExpirationDate) {
                errors.push({ label: 'CDS license expiration date', message: 'Required' });
            } else if (!this.isBeyondSubmissionThreshold(credential.cDSExpirationDate, this.MIN_EXPIRATION_DAYS_FROM_SUBMISSION)) {
                errors.push({
                    label: 'CDS license expiration date',
                    message: CONTROLLED_SUBSTANCE_EXPIRATION_MESSAGE
                });
            }

            if (!hasDoc('CDS')) {
                errors.push({ label: 'CDS Document', message: 'Document is required' });
            }
        }
        if (isDeaSelected) {

            if (!deaLicenseNumber) {
                errors.push({ label: 'DEA license number', message: 'Required' });
            } else if (!/^[A-Za-z]{2}[0-9]{7}$/.test(deaLicenseNumber)) {
                errors.push({ label: 'DEA license number', message: 'Must start with 2 letters followed by 7 digits' });
            }

            if (!credential.dEAExpirationDate) {
                errors.push({ label: 'DEA license expiration date', message: 'Required' });
            } else if (!this.isBeyondSubmissionThreshold(credential.dEAExpirationDate, this.MIN_EXPIRATION_DAYS_FROM_SUBMISSION)) {
                errors.push({
                    label: 'DEA license expiration date',
                    message: CONTROLLED_SUBSTANCE_EXPIRATION_MESSAGE
                });
            }

            if (!hasDoc('DEA')) {
                errors.push({ label: 'DEA Document', message: 'Document is required' });
            }
        }
        if (!isCdsSelected && !isDeaSelected) {
            if (!credential.painManagementNote || !credential.painManagementNote.trim()) {
                errors.push({
                    label: 'Pain Management Note',
                    message: 'Required when both CDS and DEA are not available'
                });
            }
        }
        if (userDetails.licenseState === 'SC' && demographics.specialty && demographics.specialty.toString() !== Specialities.GeneralDentistry.toString()) {

            if (!credential.specialtyLicenseNumber) {
                errors.push({ label: 'Specialty license number', message: 'Required' });
            }

            if (!credential.specialtyLicenseEffectiveDate) {
                errors.push({ label: 'Specialty license effective date', message: 'Required' });
            }

            if (!hasDoc('SPECIALTY')) {
                errors.push({ label: 'Specialty license document', message: 'Document is required' });
            }
        } else if (credential.specialtyLicenseNumber) {
            if (!credential.specialtyLicenseEffectiveDate) {
                errors.push({ label: 'Specialty license effective date', message: 'Required' });
            }
            if (!hasDoc('SPECIALTY')) {
                errors.push({ label: 'Specialty license document', message: 'Document is required' });
            }
        }

        if (credential.anesthesiaSedationLicense) {
            if (!hasDoc('ANESTHESIA')) {
                errors.push({ label: 'Anesthesia document', message: 'Document is required' });
            }
            if (!credential.anesthesiaEffectiveDate) {
                errors.push({ label: 'Anesthesia effective date', message: 'Required' });
            }
        }
        if (!credential.malpracticePolicyType) {
            errors.push({ label: 'Policy type', message: 'Required' });
        }

        if (!credential.malpracticeCarrierName) {
            errors.push({ label: 'Carrier name', message: 'Required' });
        }

        if (!credential.malpracticePolicyHolder) {
            errors.push({ label: 'Policy holder', message: 'Required' });
        } else if (!/^[A-Za-z0-9\s./-]{1,37}$/.test(credential.malpracticePolicyHolder)) {
            errors.push({ label: 'Policy holder', message: 'Can contain letters, numbers, spaces and valid symbols only' });
        }

        const malpracticeLimitPerClaim = this.parseCurrencyAmount(credential.malpracticeLimitPerClaim);
        if (this.isEmpty(credential.malpracticeLimitPerClaim)) {
            errors.push({ label: 'Limit per claim', message: 'Required' });
        }
        else if (malpracticeLimitPerClaim === null || malpracticeLimitPerClaim <= 0 || malpracticeLimitPerClaim > 1000000000) {
            errors.push({ label: 'Limit per claim', message: 'Invalid amount' });
        }

        const malpracticeLimitAggregate = this.parseCurrencyAmount(credential.malpracticeLimitAggregate);
        if (this.isEmpty(credential.malpracticeLimitAggregate)) {
            errors.push({ label: 'Limit aggregate', message: 'Required' });
        }
        else if (malpracticeLimitAggregate === null || malpracticeLimitAggregate <= 0 || malpracticeLimitAggregate > 1000000000) {
            errors.push({ label: 'Limit aggregate', message: 'Invalid amount' });
        }

        if (!credential.malpracticeExpirationDate) {
            errors.push({ label: 'Malpractice expiration date', message: 'Required' });
        } else if (!this.isBeyondSubmissionThreshold(credential.malpracticeExpirationDate, this.MIN_EXPIRATION_DAYS_FROM_SUBMISSION)) {
            errors.push({
                label: 'Malpractice expiration date',
                message: MALPRACTICE_EXPIRATION_MESSAGE
            });
        }

        if (!hasDoc('MALPRACTICE')) {
            errors.push({ label: 'Malpractice insurance document', message: 'Document is required' });
        }
        return {
            isValid: errors.length === 0,
            errors
        };
    }
    onPreview() {
        this.openPreview()
    }
    openPreview() {
        this.dialog.open(PreviewModalComponent, {
            width: '90%',
            maxHeight: '90vh',
            panelClass: 'preview-dialog'
        });
    }

    getAction(): any {
        const el = JSON.parse(localStorage.getItem('dentistInfo') || '{}') || {};
        const credentialingEvent = JSON.parse(localStorage.getItem('credentialingEvent') || '{}');
        const eventStatus = credentialingEvent?.eventStatus;

        if (!el?.reCredentialDate) {
            return false;
        }
        const recredDate: Date = this.parseYyyyMmDd("" + el.reCredentialDate);

        if (!recredDate) {
            return false;
        }
        const locations = JSON.parse(localStorage.getItem('whLocations') || '[]');
        const isNonPar =
            locations.length > 0 &&
            locations.every(
                (loc: any) =>
                    loc?.providerNetwork?.toLowerCase() === 'not participating'
            );
        if (isNonPar) {
            return false;
        }
        const today = new Date();
        const dueDate = new Date(recredDate);
        const value = Number(this.sharedService.credentialing_graceperiod);
        const gracePeriod = !value || isNaN(value) ? 180 : value;

        dueDate.setDate(dueDate.getDate() - gracePeriod);

        //  Block only if TOO EARLY (more than 180 days before)
        if (today < dueDate) {
            return false;
        }

        return true;
    }

    parseYyyyMmDd(dateString: string): Date {

        const year = Number(dateString.substring(0, 4));

        const month = Number(dateString.substring(4, 6)) - 1;

        const day = Number(dateString.substring(6, 8));

        return new Date(year, month, day);

    }
    private isMinAge(dateValue: any, minAge: number): boolean {
        if (!dateValue) return false;

        const today = new Date();
        const dob = new Date(dateValue);

        if (isNaN(dob.getTime())) {
            return false;
        }

        let age = today.getFullYear() - dob.getFullYear();
        const monthDiff = today.getMonth() - dob.getMonth();

        if (
            monthDiff < 0 ||
            (monthDiff === 0 && today.getDate() < dob.getDate())
        ) {
            age--;
        }

        return age >= minAge;
    }

    private isPastOrTodayDate(dateValue: any): boolean {
        if (!dateValue) return false;

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const inputDate = new Date(dateValue);

        if (isNaN(inputDate.getTime())) {
            return false;
        }

        return inputDate <= today;
    }

    getTin(pageNumber: number = 1, pageSize: number = 1000): Promise<any[]> {
        return new Promise((resolve, reject) => {
            this.loader.show();

            const payLoad = {
                userId: localStorage.getItem("loggedUserId") || this.reqPayload?.userId,
                credentialingEventId: localStorage.getItem("credentialingEventId"),
                pageNumber,
                pageSize
            };

            this.service.getTinDetails(payLoad).subscribe({
                next: (response: any) => {
                    this.loader.hide();

                    const mappedData = (response?.data || []).map((item: any) => ({
                        ...item,
                        IsTinFromDW: item?.isTinFromDW ? "Yes" : "No",
                        delete: (item?.isTinFromDW && !this.getDeleteStatus(item.taxId))
                            ? ''
                            : 'delete'
                    }));

                    resolve(mappedData);
                },
                error: (err: any) => {
                    this.loader.hide();
                    reject(err);
                }
            });
        });
    }

    getDeleteStatus(taxId: any) {
        const tin = taxId;
        const locations = JSON.parse(localStorage.getItem('locations') || '[]');
        const filteredLocations = locations.filter(
            (loc: any) => loc.tin === tin
        );
        const status =
            filteredLocations.length > 0 &&
            filteredLocations.every(
                (loc: any) => loc.isLocationFromTINSearch === true
            );
        if (status) {
            return true;
        }
        return false;
    }

}
